/**
 * #803 — the tab catches up when its workspace was switched somewhere else.
 *
 * The session is one cookie shared by every tab (and, on prod, by every app
 * under *.mangroveitsoftware.com), so a switch in one tab switches them all.
 * The link (`watchWorkspace` in links.ts) notices on the next answer and
 * dispatches `WORKSPACE_CHANGED_EVENT`; the API has already refused any write
 * this tab sent for the old workspace.
 *
 * WHY A RELOAD, not an in-place reset through the identity guard: the guard
 * (`useClearCacheOnIdentityChange`) resets only when the identity differs from
 * the one stored in localStorage, and the tab that switched has ALREADY stored
 * the new one there, so in this tab it would see nothing to do and leave the
 * old workspace's modules and lists on screen. The switcher itself reloads for
 * the same reason (the shell does not repaint reliably after an in-place wipe).
 * The persisted copy is dropped first, so the reload starts clean, and a
 * one-shot flag lets the app say why the page changed.
 */
import * as React from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { WORKSPACE_CHANGED_EVENT, WORKSPACE_SEED_EVENT } from '../links.js';
import { trpcPathOf } from './never-persist.js';

/** sessionStorage: this tab was reloaded because its workspace changed elsewhere. */
export const WORKSPACE_SWITCHED_NOTICE_KEY = 'mangrove.workspaceSwitchedElsewhere';

/**
 * #823: the URL parameter HQ's switcher adds when it opens another app after
 * a switch made on purpose (`?switched=1`), so that app does not take its
 * saved copy's workspace for the one it is in.
 */
export const JUST_SWITCHED_PARAM = 'switched';

/**
 * #823: a page that restores its saved copy draws a workspace before any
 * answer from the server, and a page learns its workspace only from its first
 * answer. Tell the link the workspace the restored copy shows (the saved "who
 * am I"), so the page's very first call, a save included, already says which
 * workspace it is for: if the sign-in has moved meanwhile, that save is
 * refused and the page catches up, as in #803. Returns the workspace sent, or
 * null when the restored copy names none.
 */
export function seedWorkspaceFromRestoredCache(queryClient: QueryClient): string | null {
  // A load that follows a switch made on purpose in another of our apps (HQ's
  // switcher says so in the URL): the saved copy is from the workspace left
  // behind, and the identity guard replaces it, as before. Seeding it would
  // take a deliberate switch for one made behind the page's back.
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (url.searchParams.get(JUST_SWITCHED_PARAM) === '1') {
      url.searchParams.delete(JUST_SWITCHED_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
      return null;
    }
  }
  const me = queryClient
    .getQueryCache()
    .findAll({ predicate: (q) => trpcPathOf(q.queryKey) === 'auth.getCurrentUser' })
    .map((q) => q.state.data as { tenantId?: string | null } | null | undefined)
    .find((d) => typeof d?.tenantId === 'string' && d.tenantId.length > 0);
  const tenantId = me?.tenantId ?? null;
  if (tenantId && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WORKSPACE_SEED_EVENT, { detail: { tenantId } }));
  }
  return tenantId;
}

export interface CatchUpOptions {
  queryClient: QueryClient;
  persister?: Pick<Persister, 'removeClient'> | null;
  /** Tests pass their own; the app reloads the page. */
  reload?: () => void;
  storage?: Pick<Storage, 'setItem'> | null;
}

/** Stop reads in flight, drop the persisted copy, leave a note, reload. */
export async function catchUpWithWorkspace(opts: CatchUpOptions): Promise<void> {
  await opts.queryClient.cancelQueries();
  try {
    await opts.persister?.removeClient();
  } catch {
    // The reload re-reads everything either way; a stale copy is then reset
    // by the identity guard.
  }
  try {
    const storage =
      opts.storage === undefined
        ? typeof sessionStorage === 'undefined'
          ? null
          : sessionStorage
        : opts.storage;
    storage?.setItem(WORKSPACE_SWITCHED_NOTICE_KEY, '1');
  } catch {
    // Private mode: the page still catches up, it just can not say why.
  }
  (opts.reload ?? (() => window.location.reload()))();
}

/** Mounted by `PosTrpcProvider`: answer the link's event, once per page. */
export function useCatchUpWithWorkspace(
  queryClient: QueryClient,
  persister: Pick<Persister, 'removeClient'> | null | undefined,
  reload?: () => void,
): void {
  const started = React.useRef(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChange = (): void => {
      // A burst of answers after the change reloads the page once.
      if (started.current) return;
      started.current = true;
      void catchUpWithWorkspace({
        queryClient,
        persister: persister ?? null,
        ...(reload ? { reload } : {}),
      });
    };
    window.addEventListener(WORKSPACE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(WORKSPACE_CHANGED_EVENT, onChange);
  }, [queryClient, persister, reload]);
}

/**
 * After a catch-up reload, `shown` is true once, so the app can tell the
 * person why the page changed under them and that nothing was saved.
 */
export function useWorkspaceSwitchedElsewhere(): { shown: boolean; dismiss: () => void } {
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    try {
      if (sessionStorage.getItem(WORKSPACE_SWITCHED_NOTICE_KEY) === null) return;
      sessionStorage.removeItem(WORKSPACE_SWITCHED_NOTICE_KEY);
      setShown(true);
    } catch {
      // Storage unavailable: nothing was left to show.
    }
  }, []);
  return { shown, dismiss: () => setShown(false) };
}
